/**
 * Footer column links and browse pill targets for `AppDashboardFooter`.
 * Excludes URL paths that are only category listing pages (category segments in the path).
 */

import { Platform } from 'react-native';

// The phone apps don't ship Deck, so nothing here should link into it.
const IS_TOUCH_NATIVE = Platform.OS === 'ios' || Platform.OS === 'android';

/**
 * @returns {{ title: string, links: { route: string, label: string, hint: string, icon: string }[] }[]}
 */
export function buildAppDashboardFooterSections() {
  return [
    ...(IS_TOUCH_NATIVE ? [] : [{
      title: 'Deck & recipes',
      links: [
        { route: '/decks/recipes', label: 'Deck recipes', hint: 'Template catalog', icon: 'book-open' },
        { route: '/deck', label: 'Deck workspace', hint: 'Multi-column canvas', icon: 'table-columns' },
      ],
    }]),
    {
      title: 'Models & tools',
      links: [
        { route: '/models', label: 'Models', hint: 'Browse and compare', icon: 'cube' },
        { route: '/models/search', label: 'Model search', hint: 'Find a model', icon: 'magnifying-glass' },
        { route: '/tools', label: 'AI tools', hint: 'Purpose-built flows', icon: 'wand-magic-sparkles' },
        { route: '/prompts', label: 'Prompt Library', hint: 'One-click image & video recipes', icon: 'layer-group' },
        { route: '/tools/history', label: 'Tool history', hint: 'Past runs', icon: 'clock-rotate-left' },
      ],
    },
    {
      title: 'Tasks & workflows',
      links: [
        { route: '/tasks', label: 'Tasks', hint: 'Scheduled jobs', icon: 'list-check' },
        { route: '/coworker', label: 'Work', hint: 'Hand off a goal in the cloud', icon: 'briefcase' },
        { route: '/tasks/browse', label: 'Task templates', hint: 'Curated starters', icon: 'layer-group' },
        // { route: '/workflows', label: 'Workflows', hint: 'Visual automations', icon: 'diagram-project' },
      ],
    },
    {
      title: 'Bots & explore',
      links: [
        { route: '/topics', label: 'Topics', hint: 'People, media & culture', icon: 'book' },
        { route: '/agents', label: 'Bots', hint: 'Specialized assistants', icon: 'user-astronaut' },
        { route: '/agents/search', label: 'Bot search', hint: 'Find a bot', icon: 'magnifying-glass' },
        { route: '/explore', label: 'Explore', hint: 'Gallery & inspiration', icon: 'compass' },
        { route: '/explore/categories', label: 'Explore categories', hint: 'Browse by topic', icon: 'folder-tree' },
      ],
    },
    {
      title: 'Chat & library',
      links: [
        { route: '/library', label: 'Chat library', hint: 'Saved conversations', icon: 'comments' },
        { route: '/bookmarks', label: 'Bookmarks', hint: 'Saved items', icon: 'bookmark' },
        { route: '/collections', label: 'Collections', hint: 'Organize chats', icon: 'folder' },
        { route: '/tags', label: 'Tags', hint: 'Topic tag hubs', icon: 'tag' },
      ],
    },
    {
      title: 'Files & media',
      links: [
        { route: '/files', label: 'Files', hint: 'Uploads and assets', icon: 'file' },
        { route: '/files/search', label: 'File search', hint: 'Find a file', icon: 'magnifying-glass' },
        { route: '/images', label: 'Images', hint: 'Image library', icon: 'image' },
        { route: '/videos', label: 'Videos', hint: 'Video library', icon: 'video' },
        { route: '/music', label: 'Music', hint: 'Audio library', icon: 'music' },
      ],
    },
    {
      title: 'Memory & account',
      links: [
        { route: '/memory', label: 'Memory', hint: 'Saved context', icon: 'brain' },
        { route: '/settings', label: 'Settings', hint: 'Account and preferences', icon: 'gear' },
        { route: '/referral', label: 'Referrals', hint: 'Invite credits', icon: 'user-plus' },
      ],
    },
  ];
}

/** Browse pills — aligned with `PublicFooter` browse row. */
export const HARBOR_SPOTLIGHT = [
  { route: '/tools', label: 'Tools' },
  { route: '/prompts', label: 'Prompt Library' },
  { route: '/topics', label: 'Topics' },
  { route: '/agents', label: 'Bots' },
  { route: '/tasks/browse', label: 'Task templates' },
  { route: '/decks/recipes', label: 'Deck recipes' },
  { route: '/explore', label: 'Explore' },
  { route: '/models', label: 'Models' },
  { route: '/docs/api', label: 'API Docs' },
].filter(pill => !(IS_TOUCH_NATIVE && pill.route.startsWith('/deck')));
